import React, { useState, useCallback, useEffect } from 'react';
import withTranslation from 'hoc/withTranslation';
import styled from '@emotion/styled';
import { clearLocalStorage } from 'utils/localStorageHelpers';
import { getSplunkAWSLogUrl } from 'utils/SolutionHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { setUserParam } from 'actions';
import { PANELS } from 'reducers/user';
import { StyledIcon } from 'components/Wizard/Global/WizardStyled';
import { BotIcon } from 'icons/common/Bot';
import HereLogo from '../Presentation/HereLogo';
import { fontSize, colors } from '../../global/variables';
import { isMobileDevice, isProdEnv } from '../../utils/helpers';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../utils/amplitude';
import menu from '../../global/img/menu.svg';
import mapHandler from '../../global/img/map-handler.svg';
import chevronDown from '../../global/img/chevron_down.svg';
import clearIcon from '../../global/img/clear-icon.svg';
import splunkLogo from '../../global/img/splunk_logo.svg';
import trafficLight from '../../global/img/traffic-light.svg';
import Spinner from '../Presentation/Spinner';
import { Swipeable } from '../Form/Swipeable';

const { medium } = fontSize;
const { black } = colors;

export const headerHeight = '3rem';

const StyledTopBar = styled.div(() => ({
  display: 'flex',
  height: headerHeight,
  alignItems: 'center',
  justifyContent: 'flex-end',
  '@media screen and (max-width: 850px)': {
    flexDirection: 'row-reverse',
  },
}));

const StyledTopBarContainer = styled.div(({ showTransparent, isVisible }) => ({
  position: 'fixed',
  top: 0,
  right: 0,
  left: 0,
  width: '100%',
  height: headerHeight,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: 'rgba(250, 250, 250, 0.1)',
  boxSizing: 'border-box',
  zIndex: '10000',
  paddingLeft: '2rem',
  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.15)',
  borderRadius: '0',
  textAlign: 'left',
  transition: 'all 0.5s cubic-bezier(.72,0,0,.99)',
  div: {
    lineHeight: headerHeight,
    verticalAlign: 'middle',
    transition: 'all 0.5s cubic-bezier(.72,0,0,.99)',
  },
  '@media screen and (max-width: 850px)': {
    textAlign: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    backgroundColor: showTransparent ? 'transparent' : 'rgba(250, 250, 250, 1)',
    backdropFilter: 'blur(2px)',
    top: isVisible ? '0' : 'calc(100% - 2rem)',
    div: {
      position: 'absolute',
      top: !isVisible ? '-2rem' : '0.5rem',
      transition: 'all 0.5s cubic-bezier(.72,0,0,.99)',
    },
  },
}));

const StyledTopBarTitle = styled.div({
  marginLeft: '1.1rem',
  fontWeight: 'bold',
  color: black,
  opacity: '0.8',
  fontSize: medium,
  display: 'inline-block',
  '@media screen and (max-width: 850px)': {
    display: 'none',
  },
});

const StyledHereLogo = styled(HereLogo)({
  marginLeft: '0.5rem',
  '@media screen and (max-width: 850px)': {
    marginLeft: 0,
  },
});

export const StyledToggleButton = styled.button(({ icon, rotation = '0deg' }) => ({
  display: 'flex',
  top: '0.7rem',
  backgroundColor: 'transparent',
  backgroundImage: `url(${icon})`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  border: 'none',
  opacity: 1,
  width: '1.5rem',
  height: '1.5rem',
  cursor: 'pointer',
  transition: 'all 0.1s ease-in-out',
  transform: `rotate(${rotation})`,
  '@media screen and (max-width: 850px)': {
    marginLeft: '0.7rem',
  },
  '@media screen and (min-width: 851px)': {
    marginRight: '1.4rem',
  },
}));

const StyledRoutingAiButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  marginRight: '1.4rem',
  padding: '0.35rem 0.75rem',
  border: 'none',
  borderRadius: '1rem',
  backgroundImage: isOpen
    ? 'none'
    : `linear-gradient(to right, ${colors.primaryColor}, ${colors.secondaryColor})`,
  backgroundColor: isOpen ? 'rgba(34, 205, 200, 0.15)' : 'transparent',
  color: colors.black,
  cursor: 'pointer',
  fontSize: fontSize.smaller,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  transition: 'background-color 0.2s ease, opacity 0.2s ease',
  ':hover': {
    opacity: 0.9,
  },
  '@media screen and (max-width: 850px)': {
    marginLeft: '0.7rem',
    marginRight: 0,
  },
}));

const StyledBarHandle = styled.div(({ isVisible }) => ({
  width: '3rem',
  height: '1rem',
  marginTop: isVisible ? '-1.6rem' : '2.4rem',
  backgroundColor: '#f8f9f8',
  borderRadius: '0.2rem',
  transition: 'all 0.5s cubic-bezier(.72,0,0,.99)',
  cursor: 'pointer',
  backgroundImage: `url(${mapHandler})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  '@media screen and (max-width: 850px)': isVisible
    ? {
        position: 'fixed !important',
        bottom: '3rem',
        top: 'auto !important',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '0 !important',
      }
    : {},
  '@media screen and (min-width: 851px)': {
    display: 'none',
    width: 0,
    height: 0,
  },
}));

const StyledToggleButtons = styled.div(() => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-end',
  marginLeft: 'auto',
  zIndex: 10001,
  '@media screen and (max-width: 850px)': {
    position: 'fixed',
    top: '1.5rem',
    right: '3.2rem', // leave room for the feedback/smiley icon at right: 0.4rem
    transform: 'translateY(-50%)',
    margin: 0,
    zIndex: 10001,
  },
}));

const StyledLink = styled.a({
  color: '#01b6b2',
  textDecorationLine: 'none',
  padding: '0 0.4rem 0 1rem',
  fontSize: '13px',
  ':hover': {
    color: '#00a39f',
    textDecorationLine: 'underline',
  },
});

const StyledSpan = styled.span(({ isFilename }) => ({
  display: 'inline-block',
  width: isFilename ? '6rem' : '5rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginTop: '0.4rem',
}));

const TopBar = ({
  onClickMenu,
  onToggleMobileView,
  isVisible,
  onClickTourPanel,
  isTourPanelOpen,
  showAiChat,
  solution,
  translations: {
    global: { awsLogsTrans },
    routingAiChat,
  },
}) => {
  const dispatch = useDispatch();
  const trafficFactorsEnabled = useSelector((state) => state.user.trafficFactorsEnabled);
  const trafficFetching = useSelector((state) => state.user.trafficFetching);
  const isRoutingAiChatOpen = useSelector(
    (state) => state.user.openPanel === PANELS.ROUTING_AI_CHAT,
  );
  const selectedTour = useSelector((state) => state.mapContainer.display.selectedTour);
  const isTourDetailsOpen = selectedTour !== undefined;

  const [showMapMobile, setShowMapMobile] = useState(false);
  const request = solution.requests[solution.show];

  const onClickBar = useCallback(() => {
    if (!isMobileDevice()) return;

    const component = showMapMobile ? 'map' : 'panel';
    AmplitudeService.log(AMPLITUDE_EVENTS.TOP_BAR_CLICK, { component });
    onToggleMobileView();
    setShowMapMobile(!showMapMobile);
  }, [onToggleMobileView, setShowMapMobile, showMapMobile]);

  const onClickMenuButton = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      AmplitudeService.log(AMPLITUDE_EVENTS.TOP_BAR_CLICK, { component: 'Menu' });
      onClickMenu();
    },
    [onClickMenu],
  );

  const handleToggleTrafficFactors = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const newValue = !trafficFactorsEnabled;
      dispatch(setUserParam({ trafficFactorsEnabled: newValue }));
      AmplitudeService.log(AMPLITUDE_EVENTS.TOP_BAR_CLICK, {
        component: 'Traffic Factors',
        enabled: newValue,
      });
    },
    [dispatch, trafficFactorsEnabled],
  );

  const handleOpenRoutingAiChat = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const nextOpenState = !isRoutingAiChatOpen;
      dispatch(
        setUserParam({
          openPanel: nextOpenState ? PANELS.ROUTING_AI_CHAT : undefined,
        }),
      );
      AmplitudeService.log(AMPLITUDE_EVENTS.TOP_BAR_CLICK, {
        component: 'Routing AI',
        enabled: nextOpenState,
      });
    },
    [dispatch, isRoutingAiChatOpen],
  );

  useEffect(() => {
    getSplunkAWSLogUrl(dispatch, request, isProdEnv(), solution.show);
  }, [request, dispatch, solution.show]);

  return (
    <StyledTopBar>
      <Swipeable onSwipedUp={onClickBar} onSwipedDown={onClickBar}>
        <StyledTopBarContainer
          onClick={onClickBar}
          showTransparent={!showMapMobile}
          isVisible={isVisible}
        >
          <StyledTopBarTitle data-test-id="HereTitle">HERE Tour Planning</StyledTopBarTitle>
          {request?.splunkLogUrl && (
            <>
              <StyledLink
                href={request.splunkLogUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={request.splunkLogUrl}
              >
                <StyledSpan title={request.splunkLogUrl}>{`${awsLogsTrans} `}</StyledSpan>
                <StyledSpan isFilename title={request.name}>
                  {request.name}
                </StyledSpan>
              </StyledLink>
              <StyledIcon iconUrl={splunkLogo} huge logo />
            </>
          )}
          <StyledBarHandle isVisible={!isVisible} />
        </StyledTopBarContainer>
      </Swipeable>
      <StyledHereLogo data-test-id="HereLogo" />
      <StyledToggleButtons>
        {showAiChat && (
          <StyledRoutingAiButton
            type="button"
            onClick={handleOpenRoutingAiChat}
            isOpen={isRoutingAiChatOpen}
            aria-label={routingAiChat.askButton}
            data-testid="routing-ai-entry-point"
          >
            <BotIcon size={14} />
            {routingAiChat.askButton}
          </StyledRoutingAiButton>
        )}
        <StyledToggleButton
          data-test-id="clearLocalStorage"
          type="button"
          onClick={clearLocalStorage}
          icon={clearIcon}
          title="clear local storage"
        />
        {trafficFetching ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '1.5rem',
              height: '1.5rem',
              marginRight: '1.4rem',
              overflow: 'hidden',
            }}
          >
            <div style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}>
              <Spinner color="#007acc" />
            </div>
          </div>
        ) : (
          <StyledToggleButton
            data-test-id="toggleTrafficFactors"
            type="button"
            onClick={isTourDetailsOpen ? handleToggleTrafficFactors : undefined}
            icon={trafficLight}
            aria-label="Toggle traffic factors"
            title={
              isTourDetailsOpen
                ? 'Toggle traffic factors'
                : 'Open tour details to toggle traffic factors'
            }
            style={{
              opacity: isTourDetailsOpen ? (trafficFactorsEnabled ? 1 : 0.4) : 0.2,
              filter: isTourDetailsOpen && trafficFactorsEnabled ? 'none' : 'grayscale(100%)',
              cursor: isTourDetailsOpen ? 'pointer' : 'not-allowed',
              pointerEvents: isTourDetailsOpen ? 'auto' : 'none',
            }}
          />
        )}
        <StyledToggleButton
          data-test-id="toggleArrow"
          type="button"
          onClick={onClickTourPanel}
          icon={chevronDown}
          rotation={isTourPanelOpen ? '180deg' : '0deg'}
        />
        <StyledToggleButton
          type="button"
          onClick={onClickMenuButton}
          icon={menu}
          data-testid="menuBtn"
        />
      </StyledToggleButtons>
    </StyledTopBar>
  );
};

export default withTranslation(TopBar);
